import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PlusIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../context/AuthContext';

// Color options for Color Code select
const colorOptions = [
  { value: '#FF0000', label: 'Red' },
  { value: '#00FF00', label: 'Green' },
  { value: '#0000FF', label: 'Blue' },
  { value: '#FFFF00', label: 'Yellow' },
  { value: '#FF00FF', label: 'Magenta' },
  { value: '#00FFFF', label: 'Cyan' },
  { value: '#FFA500', label: 'Orange' },
  { value: '#800080', label: 'Purple' },
  { value: '#008000', label: 'Dark Green' },
  { value: '#000080', label: 'Navy' }
];

// Options for "Can View Schedules Of" and "Can View Locations Of"
const viewOptions = [
  { value: 'all', label: 'All Employees' },
  { value: 'team', label: 'Team Members Only' },
  { value: 'none', label: 'None' }
];

// Add gender options at the top with other options
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

// Validation schema
const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  phone: yup.string()
    .required('Phone number is required')
    .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in format (xxx) xxx-xxxx'),
  gender: yup.string().required('Gender is required'),
  dateOfBirth: yup.date().required('Date of birth is required'),
  shortBio: yup.string(),
  isFieldWorker: yup.boolean(),
  colorCode: yup.string().required('Color code is required'),
  nameOnWorkOrders: yup.string().required('Name to display on work orders is required'),
  nameOnDispatch: yup.string().required('Name to display on dispatch is required'),
  canViewSchedulesOf: yup.string().required('Schedule view permission is required'),
  canViewLocationsOf: yup.string().required('Location view permission is required'),
  canChangeAssignments: yup.boolean(),
  canViewUnassigned: yup.boolean(),
  ssn: yup.string(),
  driversLicenseNumber: yup.string().required('Drivers License Number is required'),
  driversLicenseExpiration: yup.date().required('Drivers License Expiration is required'),
  otherId1: yup.string(),
  otherId2: yup.string(),
  otherId3: yup.string()
});

const Profile = () => {
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
    watch,
    trigger,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isFieldWorker: false,
      canChangeAssignments: false,
      canViewUnassigned: false,
      colorCode: '',
      canViewSchedulesOf: '',
      canViewLocationsOf: ''
    }
  });

  // Load initial data from localStorage
  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem('auth'));
    if (authData) {
      // Split name into first and last name
     
      const firstName = authData.first_name;
      const lastName = authData.last_name;

      // Format date for date input
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      // Set form values with proper defaults
      reset({
        firstName,
        lastName,
        email: authData.email,
        // Fill in fields from auth data if they exist
        gender: authData.gender === 'mal' ? 'male' : authData.gender || '',
        phone: authData.phone || '',
        dateOfBirth: formatDateForInput(authData.dateOfBirth),
        // Set other default values
        isFieldWorker: authData.isFieldWorker || false,
        canChangeAssignments: authData.canChangeAssignments || false,
        canViewUnassigned: authData.canViewUnassigned || false,
        colorCode: authData.colorCode || colorOptions[0].value,
        nameOnWorkOrders: authData.nameOnWorkOrders || `${firstName} ${lastName}`,
        nameOnDispatch: authData.nameOnDispatch || `${firstName} ${lastName}`,
        canViewSchedulesOf: authData.canViewSchedulesOf || 'none',
        canViewLocationsOf: authData.canViewLocationsOf || 'none',
        shortBio: authData.shortBio || '',
        ssn: authData.ssn || '',
        driversLicenseNumber: authData.driversLicenseNumber || '',
        driversLicenseExpiration: formatDateForInput(authData.driversLicenseExpiration),
        otherId1: authData.otherId1 || '',
        otherId2: authData.otherId2 || '',
        otherId3: authData.otherId3 || ''
      });

      // Set profile photo
      setProfilePhoto(authData.avatar);
    }
  }, [reset]);

  // Watch the select values to track changes
  const colorCodeValue = watch('colorCode');
  const canViewSchedulesOfValue = watch('canViewSchedulesOf');
  const canViewLocationsOfValue = watch('canViewLocationsOf');

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    
    // Remove all non-digit characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Format the phone number
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Handle phone number input change
  const handlePhoneChange = (e) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setValue('phone', formattedPhoneNumber);
    // Trigger validation after setting the value
    trigger('phone');
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Log form data and validation state
      console.log('Form Data:', data);
      console.log('Form Errors:', errors);
      console.log('Touched Fields:', touchedFields);
      
      // Validate all fields
      const isValid = await trigger();
      console.log('Form Validation Result:', isValid);
      
      if (!isValid) {
        console.log('Validation failed. Please check all required fields.');
        return;
      }
      
      // Get current auth data
      const authData = JSON.parse(localStorage.getItem('auth'));
      
      // Update auth data with new profile information
      const updatedAuthData = {
        ...authData,
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        avatar: profilePhoto || authData.avatar,
        
        ...authData.profile, // Keep existing profile data
        ...data, // Update with new form data
        
        // Ensure these fields are properly set
        canViewUnassigned: data.canViewUnassigned || false,
        canChangeAssignments: data.canChangeAssignments || false,
        canViewLocationsOf: data.canViewLocationsOf || 'none',
        canViewSchedulesOf: data.canViewSchedulesOf || 'none',
        colorCode: data.colorCode || '#FF0000',
        dateOfBirth: data.dateOfBirth,
        
        firstName: data.firstName,
        gender: data.gender,
        isFieldWorker: data.isFieldWorker || false,
        lastName: data.lastName,
        nameOnDispatch: data.nameOnDispatch || `${data.firstName} ${data.lastName}`,
        nameOnWorkOrders: data.nameOnWorkOrders || `${data.firstName} ${data.lastName}`,
        phone: data.phone,
        role: authData.role // Keep existing role
         
      };

      // Save to localStorage
      localStorage.setItem('auth', JSON.stringify(updatedAuthData));
      login(updatedAuthData);
      // Show success message
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePhoto(reader.result); // Base64 string
    };
    reader.readAsDataURL(file); // Reads file as Base64
  }
};


  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Profile</h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Information Section */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            
            {/* Profile Photo */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={profilePhoto || '/default-avatar.png'}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-1 rounded-full cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <PlusIcon className="h-4 w-4" />
                </label>
              </div>
            </div>

            {/* Name and Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  {...register('firstName')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  {...register('lastName')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  {...register('email')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  {...register('phone')}
                  onChange={handlePhoneChange}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.phone && touchedFields.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="(xxx) xxx-xxxx"
                  maxLength="14"
                />
                {errors.phone && touchedFields.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>
            </div>

            {/* Gender and Date of Birth */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <Select
                  options={genderOptions}
                  onChange={(selected) => setValue('gender', selected.value)}
                  value={genderOptions.find(option => option.value === watch('gender'))}
                  className={`w-full ${errors.gender ? 'border-red-500' : ''}`}
                  classNamePrefix="select"
                  placeholder="Select gender"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.gender ? '#EF4444' : state.isFocused ? '#3B82F6' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                      '&:hover': {
                        borderColor: errors.gender ? '#EF4444' : '#3B82F6'
                      },
                      minHeight: '42px'
                    })
                  }}
                />
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                <input
                  type="date"
                  {...register('dateOfBirth')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.dateOfBirth ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter date of birth"
                />
                {errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth.message}</p>
                )}
              </div>
            </div>

            {/* Short Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Short Bio</label>
              <textarea
                {...register('shortBio')}
                rows="3"
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                  errors.shortBio ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder="Enter a short bio"
              />
              {errors.shortBio && (
                <p className="mt-1 text-sm text-red-600">{errors.shortBio.message}</p>
              )}
            </div>
          </div>

          {/* Field Worker Settings */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Field Worker Settings</h2>
            
            {/* Field Worker Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isFieldWorker')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                This Employee Is A Field Worker
              </label>
            </div>

            {/* Color Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Color Code</label>
              <Select
                options={colorOptions}
                onChange={(selected) => setValue('colorCode', selected.value)}
                value={colorOptions.find(option => option.value === colorCodeValue)}
                className={`w-full ${errors.colorCode && touchedFields.colorCode ? 'border-red-500' : ''}`}
                classNamePrefix="select"
                placeholder="Select a color"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: errors.colorCode && touchedFields.colorCode ? '#EF4444' : state.isFocused ? '#3B82F6' : '#D1D5DB',
                    boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                    '&:hover': {
                      borderColor: errors.colorCode && touchedFields.colorCode ? '#EF4444' : '#3B82F6'
                    },
                    minHeight: '42px'
                  })
                }}
              />
              {errors.colorCode && touchedFields.colorCode && (
                <p className="mt-1 text-sm text-red-600">{errors.colorCode.message}</p>
              )}
            </div>

            {/* Display Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name To Display On Work Orders</label>
                <input
                  type="text"
                  {...register('nameOnWorkOrders')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.nameOnWorkOrders ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter name to display on work orders"
                />
                {errors.nameOnWorkOrders && (
                  <p className="mt-1 text-sm text-red-600">{errors.nameOnWorkOrders.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name To Display On Dispatch</label>
                <input
                  type="text"
                  {...register('nameOnDispatch')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.nameOnDispatch ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter name to display on dispatch"
                />
                {errors.nameOnDispatch && (
                  <p className="mt-1 text-sm text-red-600">{errors.nameOnDispatch.message}</p>
                )}
              </div>
            </div>

            {/* View Permissions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Can View Schedules Of</label>
                <Select
                  options={viewOptions}
                  onChange={(selected) => setValue('canViewSchedulesOf', selected.value)}
                  value={viewOptions.find(option => option.value === canViewSchedulesOfValue)}
                  className={`w-full ${errors.canViewSchedulesOf && touchedFields.canViewSchedulesOf ? 'border-red-500' : ''}`}
                  classNamePrefix="select"
                  placeholder="Select permission"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.canViewSchedulesOf && touchedFields.canViewSchedulesOf ? '#EF4444' : state.isFocused ? '#3B82F6' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                      '&:hover': {
                        borderColor: errors.canViewSchedulesOf && touchedFields.canViewSchedulesOf ? '#EF4444' : '#3B82F6'
                      },
                      minHeight: '42px'
                    })
                  }}
                />
                {errors.canViewSchedulesOf && touchedFields.canViewSchedulesOf && (
                  <p className="mt-1 text-sm text-red-600">{errors.canViewSchedulesOf.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Can View Locations Of</label>
                <Select
                  options={viewOptions}
                  onChange={(selected) => setValue('canViewLocationsOf', selected.value)}
                  value={viewOptions.find(option => option.value === canViewLocationsOfValue)}
                  className={`w-full ${errors.canViewLocationsOf && touchedFields.canViewLocationsOf ? 'border-red-500' : ''}`}
                  classNamePrefix="select"
                  placeholder="Select permission"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.canViewLocationsOf && touchedFields.canViewLocationsOf ? '#EF4444' : state.isFocused ? '#3B82F6' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                      '&:hover': {
                        borderColor: errors.canViewLocationsOf && touchedFields.canViewLocationsOf ? '#EF4444' : '#3B82F6'
                      },
                      minHeight: '42px'
                    })
                  }}
                />
                {errors.canViewLocationsOf && touchedFields.canViewLocationsOf && (
                  <p className="mt-1 text-sm text-red-600">{errors.canViewLocationsOf.message}</p>
                )}
              </div>
            </div>

            {/* Additional Toggles */}
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('canChangeAssignments')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Allow To Change Job/Estimate Assignment
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('canViewUnassigned')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Allow To View Unassigned Jobs/Estimates
                </label>
              </div>
            </div>
          </div>

          {/* Identification Information */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Identification Information</h2>
            
            {/* SSN and Driver's License */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">SSN</label>
                <input
                  type="text"
                  {...register('ssn')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.ssn ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter SSN"
                />
                {errors.ssn && (
                  <p className="mt-1 text-sm text-red-600">{errors.ssn.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Driver's License Number</label>
                <input
                  type="text"
                  {...register('driversLicenseNumber')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.driversLicenseNumber ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter drivers license number"
                />
                {errors.driversLicenseNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.driversLicenseNumber.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Driver's License Expiration Date</label>
                <input
                  type="date"
                  {...register('driversLicenseExpiration')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.driversLicenseExpiration ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter drivers license expiration date"
                />
                {errors.driversLicenseExpiration && (
                  <p className="mt-1 text-sm text-red-600">{errors.driversLicenseExpiration.message}</p>
                )}
              </div>
            </div>

            {/* Other IDs */}
            <div className="space-y-4">
              {[1, 2, 3].map((idNum) => (
                <div key={idNum} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Other ID {idNum}</label>
                    <input
                      type="text"
                      {...register(`otherId${idNum}`)}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                        errors[`otherId${idNum}`] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                      }`}
                      placeholder={`Enter other ID ${idNum}`}
                    />
                    {errors[`otherId${idNum}`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`otherId${idNum}`].message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile; 